"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { MapPin, CircleCheck as CheckCircle2, ArrowRight, Calendar, ShieldCheck, TrendingUp, UserCheck, Star, Search } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const trustHighlights = [{
  title: "100% Direct Access",
  desc: "You work directly with me, Yassine Essaadi—never an account manager."
}, {
  title: "Tailored Dundee Strategy",
  desc: "Custom local search campaigns built for the Scottish market."
}, {
  title: "Transparent Measurement",
  desc: "Clear monthly reporting focused on real lead revenue growth."
}];
const localPillars = ["Google Map Pack Rankings", "Localized On-Page SEO", "High-Authority Local Backlinks", "Google Business Profile Optimization"];
export default function Localhero() {
  const shouldReduceMotion = useReducedMotion();
  const fadeInVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: (custom: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        delay: shouldReduceMotion ? 0 : custom * 0.1,
        ease: "easeOut"
      }
    })
  };
  return <section id="localherodundee" data-nav="dark" className="relative w-full bg-primary text-primary-foreground py-20 lg:py-28 overflow-hidden">
      {/* Background Subtle Gradients & Grid Pattern */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,var(--color-accent)_0%,transparent_35%)] opacity-20 pointer-events-none" />
      <div className="absolute inset-0 bg-[linear-gradient(to_right,hsl(0_0%_100%/0.03)_1px,transparent_1px),linear-gradient(to_bottom,hsl(0_0%_100%/0.03)_1px,transparent_1px)] bg-[size:32px_32px] pointer-events-none" />

      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          {/* Left Column: Heading & Key Value Proposition */}
          <div className="lg:col-span-7 flex flex-col items-start space-y-6">
            
            {/* Location / Expert Badge */}
            <motion.div custom={0} initial="hidden" animate="visible" variants={fadeInVariants}>
              <Badge variant="secondary" className="bg-secondary text-secondary-foreground hover:bg-secondary/90 px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium tracking-wide flex items-center gap-2">
                <MapPin className="w-4 h-4 text-accent shrink-0" />
                <span>Local SEO Specialist in Dundee, Scotland</span>
              </Badge>
            </motion.div>

            {/* Main H1 Title */}
            <motion.h1 custom={1} initial="hidden" animate="visible" variants={fadeInVariants} className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-primary-foreground leading-[1.15]">
              Local SEO Consultant in Dundee
            </motion.h1>

            {/* Sub-headline Narrative */}
            <motion.p custom={2} initial="hidden" animate="visible" variants={fadeInVariants} className="font-sans text-base sm:text-lg text-primary-foreground/90 max-w-2xl leading-relaxed">
              I help Dundee businesses capture local search demand, dominate the Google Map Pack, and convert high-intent Scottish customers. No junior account managers, no generic checklists—just my hands-on SEO strategy tailored specifically to your growth goals.
            </motion.p>

            {/* Core Local Deliverables */}
            <motion.div custom={3} initial="hidden" animate="visible" variants={fadeInVariants} className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full max-w-xl pt-2">
              {localPillars.map((pillar, i) => <div key={i} data-index={i} className="flex items-center space-x-2">
                  <CheckCircle2 className="w-4 h-4 text-accent shrink-0" />
                  <span className="text-sm font-medium text-primary-foreground/95">
                    {pillar}
                  </span>
                </div>)}
            </motion.div>

            {/* Hero CTAs */}
            <motion.div custom={4} initial="hidden" animate="visible" variants={fadeInVariants} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full sm:w-auto pt-4">
              <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-md font-semibold text-base shadow-md transition-colors">
                <Link to="/ContactPage" className="flex items-center justify-center gap-2">
                  <SafeHydrate><Calendar className="w-5 h-5" /></SafeHydrate>
                  <span>Book a Free SEO Consultation</span>
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="border-primary-foreground/30 bg-primary-foreground/10 text-primary-foreground hover:bg-primary-foreground/20 rounded-md font-medium text-base transition-colors">
                <Link to="/CaseStudiesPage" className="flex items-center justify-center gap-2">
                  <span>Explore My Case Studies</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </Button>
            </motion.div>

            {/* Live Trust Micro-text */}
            <motion.div custom={5} initial="hidden" animate="visible" variants={fadeInVariants} className="flex items-center gap-3 pt-2 text-xs sm:text-sm text-primary-foreground/80">
              <div className="flex -space-x-1">
                <span className="inline-block w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              </div>
              <span>Direct 1-on-1 consultant access with Yassine Essaadi</span>
            </motion.div>

          </div>

          {/* Right Column: High-Trust Personal Consultant Profile Card */}
          <div className="lg:col-span-5 relative">
            <motion.div initial={{
            opacity: 0,
            scale: shouldReduceMotion ? 1 : 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.6,
            ease: "easeOut",
            delay: 0.2
          }} className="relative bg-card text-card-foreground rounded-2xl p-6 sm:p-8 shadow-2xl border border-border">
              {/* Profile Header */}
              <div className="flex flex-col sm:flex-row items-center sm:items-start gap-5">
                <div className="relative shrink-0">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_seo_consultant.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_seo_consultant.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_seo_consultant.webp-wvc-sizes" alt="Yassine Essaadi - Local SEO Consultant Dundee" loading="lazy" decoding="async" className="w-24 h-24 sm:w-28 sm:h-28 rounded-full object-cover border-2 border-primary shadow-md" />
                  <div className="absolute -bottom-1 -right-1 bg-accent text-accent-foreground rounded-full p-1.5 shadow-sm" title="Verified Independent Expert">
                    <ShieldCheck className="w-4 h-4" />
                  </div>
                </div>

                <div className="text-center sm:text-left space-y-1">
                  <Badge variant="outline" className="border-primary/20 text-primary text-xs font-semibold mb-1">
                    Independent SEO Expert
                  </Badge>
                  <h2 className="font-serif text-xl sm:text-2xl font-bold text-foreground">
                    Yassine Essaadi
                  </h2>
                  <p className="text-sm font-medium text-muted-foreground">
                    Founder & Lead SEO Consultant
                  </p>
                  <div className="flex items-center justify-center sm:justify-start gap-1 pt-1 text-amber-500">
                    <Star className="w-4 h-4 fill-amber-500" />
                    <Star className="w-4 h-4 fill-amber-500" />
                    <Star className="w-4 h-4 fill-amber-500" />
                    <Star className="w-4 h-4 fill-amber-500" />
                    <Star className="w-4 h-4 fill-amber-500" />
                    <span className="text-xs text-muted-foreground ml-1 font-sans font-normal">(Proven Scottish Results)</span>
                  </div>
                </div>
              </div>

              <Separator className="my-6" />

              {/* Personal Guarantee / Focus Points */}
              <div className="space-y-4">
                <p className="text-xs sm:text-sm text-foreground/90 leading-relaxed italic bg-muted/50 p-3.5 rounded-lg border border-border/50">
                  "I don't delegate your SEO to junior staff. When you partner with YE Consultancy, every strategic decision, technical audit, and content road map is handled directly by me."
                </p>

                <div className="space-y-3 pt-2">
                  <div className="flex items-start gap-3">
                    <UserCheck className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                    <div>
                      <h3 className="text-sm font-semibold text-foreground">Zero Agency Bloat</h3>
                      <p className="text-xs text-muted-foreground">Direct communication, rapid implementation, and focused deliverables.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <TrendingUp className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                    <div>
                      <h4 className="text-sm font-semibold text-foreground">Focus on ROI & Leads</h4>
                      <p className="text-xs text-muted-foreground">Rankings are useless if they don't generate phone calls and local enquiries.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Search className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                    <div>
                      <h4 className="text-sm font-semibold text-foreground">Dundee Market Insights</h4>
                      <p className="text-xs text-muted-foreground">Tailored search strategies geared towards Tayside and Scottish audiences.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Card Footer Link */}
              <div className="mt-6 pt-4 border-t border-border">
                <Button asChild variant="default" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-medium text-sm rounded-md shadow-sm">
                  <Link to="/LocalSEOPage" className="flex items-center justify-center gap-2">
                    <span>Learn More About Local SEO Services</span>
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </Button>
              </div>

            </motion.div>
          </div>

        </div>

        {/* Bottom Trust Signals Strip */}
        <div className="mt-16 sm:mt-20 pt-10 border-t border-primary-foreground/15 grid grid-cols-1 md:grid-cols-3 gap-8">
          {trustHighlights.map((item, idx) => <div key={idx} data-index={idx} className="flex items-start space-x-3">
              <div className="p-2 rounded-lg bg-primary-foreground/10 text-accent shrink-0">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <div>
                <h4 className="text-sm font-bold text-primary-foreground">{item.title}</h4>
                <p className="text-xs text-primary-foreground/80 mt-1 leading-normal">{item.desc}</p>
              </div>
            </div>)}
        </div>

      </div>
    </section>;
}